import { useTooltipsEnabled } from './tooltip-preference';
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

/** A viewport-owned tooltip cannot be clipped by a scrolling field/thumbnail pane. */
export const TooltipButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { tooltip: string; helpOnClick?: boolean }
>(function TooltipButton(
  {
    children,
    tooltip,
    helpOnClick = false,
    onMouseEnter,
    onMouseLeave,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
    onFocus,
    onBlur,
    onClick,
    onKeyDown,
    ...props
  },
  forwardedRef,
) {
  const enabled = useTooltipsEnabled();
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  useImperativeHandle(forwardedRef, () => button.current!, []);
  const bubble = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerActivated = useRef(false);
  const cancelHover = () => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  const dismiss = () => {
    cancelHover();
    setOpen(false);
    setPinned(false);
  };
  const leave = () => {
    if (!pinned) dismiss();
  };
  const offerHover = () => {
    if (!enabled || hoverTimer.current !== null) return;
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      setOpen(true);
    }, 500);
  };
  useEffect(() => () => cancelHover(), []);
  useEffect(() => {
    if (!enabled) dismiss();
  }, [enabled]);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  useLayoutEffect(() => {
    const place = () => {
      if (!button.current || !bubble.current) return;
      const anchor = button.current.getBoundingClientRect(),
        tip = bubble.current.getBoundingClientRect();
      setPosition({
        left: Math.max(
          8,
          Math.min(innerWidth - tip.width - 8, anchor.left + (anchor.width - tip.width) / 2),
        ),
        top:
          anchor.top >= tip.height + 16
            ? anchor.top - tip.height - 8
            : Math.max(8, Math.min(innerHeight - tip.height - 8, anchor.bottom + 8)),
      });
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Explicitly opened help owns the first Escape, just like a small popover.
        if (pinned) {
          event.preventDefault();
          event.stopPropagation();
        }
        dismiss();
      }
    };
    const outside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !button.current?.contains(event.target) &&
        !bubble.current?.contains(event.target)
      )
        dismiss();
    };
    if (open) place();
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    // Dialogs may stop Escape bubbling to protect their navigation owner.
    // Observe it without consuming it so hover help still dismisses there.
    window.addEventListener('keydown', escape, true);
    if (helpOnClick && open) window.addEventListener('pointerdown', outside, true);
    return () => {
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('keydown', escape, true);
      window.removeEventListener('pointerdown', outside, true);
    };
  }, [open, pinned, tooltip, helpOnClick]);
  const visible = open && (enabled || pinned);
  return (
    <>
      <button
        {...props}
        ref={button}
        aria-expanded={helpOnClick ? visible : props['aria-expanded']}
        aria-describedby={
          [props['aria-describedby'], visible ? id : undefined].filter(Boolean).join(' ') ||
          undefined
        }
        // React suppresses mouse-enter on disabled buttons, but their unavailable reason still needs hover help.
        onPointerEnter={(event) => {
          if (event.pointerType !== 'touch') offerHover();
          onPointerEnter?.(event);
        }}
        onPointerLeave={(event) => {
          pointerActivated.current = false;
          leave();
          onPointerLeave?.(event);
        }}
        onMouseEnter={(event) => {
          offerHover();
          onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          leave();
          onMouseLeave?.(event);
        }}
        onPointerDown={(event) => {
          pointerActivated.current = true;
          if (!helpOnClick) dismiss();
          onPointerDown?.(event);
        }}
        onFocus={(event) => {
          if (
            enabled &&
            !pointerActivated.current &&
            event.currentTarget.matches(':focus-visible')
          ) {
            cancelHover();
            setOpen(true);
          }
          onFocus?.(event);
        }}
        onBlur={(event) => {
          dismiss();
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          pointerActivated.current = false;
          if (!helpOnClick || event.key === 'Escape') dismiss();
          onKeyDown?.(event);
        }}
        onClick={(event) => {
          if (helpOnClick) {
            cancelHover();
            setOpen(!pinned);
            setPinned(!pinned);
          } else dismiss();
          onClick?.(event);
        }}
      >
        {children}
      </button>
      {/* Native modal dialogs live above document.body's stacking contexts.
        Keep their help in the same top-layer owner, not behind its backdrop. */}
      {visible &&
        tooltip &&
        createPortal(
          <span ref={bubble} id={id} role="tooltip" className="workspace-tooltip" style={position}>
            {tooltip}
          </span>,
          button.current?.closest('dialog[open]') ?? document.body,
        )}
    </>
  );
});
